/**
 * Menu 4 (DATA) — /api/employer/data. Alle aggregaten komen al door de
 * privacy-grendels van de server; onderdrukte data zit niet in de respons.
 */
import api from './api';

export interface Periode {
  van: string;
  tot: string;
}

export interface Grendel {
  onderdrukt: boolean;
  reden: 'MELDINGEN' | 'FTE' | 'RESPONSES' | 'REVIEWS' | null;
  drempel: number | null;
}

const d = <T,>(r: { data: { data: T } }) => r.data.data;
const BASE = '/employer/data';

// ─── Submap 1 ─────────────────────────────────────────────────────────────────

export interface VerzuimMaand {
  maand: string;
  verzuim_pct: number | null;
  bron: 'payroll' | 'ziekmeldingen' | null;
  zieke_medewerkers: number;
  nieuwe_ziekmeldingen: number;
  medewerker_meldingen: number;
}

export interface Overtreding {
  case_code: string;
  mijlpaal: string | null;
  deadline: string | null;
  dagen_over_tijd: number | null;
  vestiging: string | null;
  afdeling: string | null;
}

export interface VerzuimData {
  periode: Periode;
  groep: { departmentId: string | null; locationId: string | null };
  scope_vast: boolean;
  afdelingen: { id: string; naam: string }[];
  fte: { fte: number | null; bron: 'payroll' | 'profiel' | null };
  grendel: Grendel;
  trend: VerzuimMaand[];
  gemiddeld_pct: number | null;
  nieuwe_ziekmeldingen: number | null;
  correlatie_beschikbaar: boolean;
  risicometer: {
    segment: 'MKB' | 'ENTERPRISE';
    fte: number | null;
    enterprise_grens: number;
    bedrag_per_overtreding: number;
    aantal: number;
    totaal: number;
    overtredingen: Overtreding[];
  };
  doorlooptijd: { aantal: number | null; gem_dagen: number | null; wettelijke_termijn_dagen: number; grendel: Grendel };
}

// ─── Submap 2 ─────────────────────────────────────────────────────────────────

export interface CrisisIncident {
  id: string;
  incident_label: string;
  vestiging: string | null;
  vestiging_plaats: string | null;
  incident_datum: string;
  dagdeel: string;
  status: string;
  inzet_type: 'SINGLE' | 'DUAL_RESPONSE';
  responders: number;
  uren_gefactureerd: number;
  uren_definitief: boolean;
  escalatie_label: string | null;
  reis_overloop: boolean;
  reis_km_totaal: number;
  reistijd_forfait_uren: number;
  reiskosten: number;
  dekking: { type: 'VOLLEDIG' | 'GEDEELTELIJK' | 'GEEN'; gewenst: number; gedekt: number; gefactureerd: number };
  factuur_bedrag: number;
  kosten_totaal: number;
  factuur_status: string;
}

export interface CrisisData {
  periode: Periode;
  fonds: { module_actief: boolean; resterend: number; vrije_uitrukken: number; gebruikte_uitrukken: number; abonnementen: number };
  on_demand_uurtarief: number;
  min_uren_pp: number;
  totalen: {
    incidenten: number;
    single: number;
    dual: number;
    responders: number;
    uren_gefactureerd: number;
    reis_km: number;
    reistijd_uren: number;
    reiskosten: number;
    kosten_totaal: number;
    on_demand_bedrag: number;
    volledig_gedekt: number;
    on_demand: number;
  };
  logboek: CrisisIncident[];
}

// ─── Submap 3 ─────────────────────────────────────────────────────────────────

export interface CashflowMaand {
  maand: string;
  preventief: number;
  arbo: number;
  crisis: number;
}

export interface ProjectMijlpaalCap {
  milestone_id: string;
  mijlpaal: string;
  project: string;
  professional: string | null;
  status: string;
  geblokkeerd: boolean;
  budget_uren: number;
  geschreven_uren: number;
  extra_uren_toegekend: number;
  verbruik_pct: number | null;
  cap_bereikt_op: string | null;
}

export interface BudgettenData {
  periode: Periode;
  wallet: {
    model: string;
    bron: 'payroll' | 'cao' | 'budget_per_medewerker' | null;
    medewerkers: number;
    gealloceerd: number | null;
    geconsumeerd: number;
    boekingen: number;
    resterend: number | null;
    verbruik_pct: number | null;
  };
  cashflow: { maanden: CashflowMaand[]; totalen: { preventief: number; arbo: number; crisis: number }; buiten_grafiek: number };
  projecten: ProjectMijlpaalCap[];
}

// ─── Submap 4 ─────────────────────────────────────────────────────────────────

export interface HittekaartBron {
  id: string;
  soort: 'campagne' | 'thermometer';
  titel: string;
  start_op: string | null;
  eind_op: string | null;
  status: string;
}

export interface Hittekaart {
  bron: { soort: 'campagne' | 'thermometer'; id: string; titel: string };
  kolommen: { key: string; label: string }[];
  rijen: { soort: 'organisatie' | 'afdeling'; id: string | null; naam: string; aantal: number | null; scores: (number | null)[] | null }[];
  minimum_responses: number;
  organisatie_onderdrukt: boolean;
  afdelingen_samengevoegd: number;
}

export interface OpenAntwoorden {
  onderdrukt: boolean;
  minimum_responses: number;
  minimum_teksten: number;
  vragen: {
    id: string;
    tekst: string;
    antwoorden: string[];
    /** Alleen afdelingen die door de tekst-grendel komen (≥ 5 teksten, ≥ 10 responses). */
    afdelingen: { id: string; naam: string; antwoorden: string[] }[];
    afdelingen_vergrendeld: number;
  }[];
}

export type CampagneStatus = 'CONCEPT' | 'GEPLAND' | 'LIVE' | 'AFGESLOTEN' | 'GEANNULEERD';
export type Moment = 'VOORAANKONDIGING' | 'LIVEGANG' | 'HERINNERING';

export interface CampagneRij {
  id: string;
  titel: string;
  soort: 'STANDAARD' | 'CUSTOM';
  status: CampagneStatus;
  start_op: string | null;
  eind_op: string | null;
  doelgroep: string;
  aantal_vragen: number;
  doelgroep_omvang: number;
  deelnames: number;
  respons_pct: number | null;
}

export interface CommunicatieBericht {
  moment: Moment;
  onderwerp: string;
  inhoud_html: string;
  sms_tekst: string | null;
  verzonden_op?: string | null;
  aantal_verzonden?: number | null;
}

export interface CampagneDetail {
  id: string;
  titel: string;
  soort: 'STANDAARD' | 'CUSTOM';
  status: CampagneStatus;
  location_id: string | null;
  department_id: string | null;
  start_op: string | null;
  eind_op: string | null;
  kanaal_email: boolean;
  kanaal_sms: boolean;
  vragen: { id: string; volgorde: number; type: 'STERREN' | 'OPEN'; tekst: string; label_laag: string | null; label_hoog: string | null }[];
  communicatie: CommunicatieBericht[];
  doelgroep_omvang: number;
  deelnames: number;
  respons_pct: number | null;
}

export interface VraagInvoer {
  type: 'STERREN' | 'OPEN';
  tekst: string;
  label_laag?: string | null;
  label_hoog?: string | null;
}

export interface CampagneInvoer {
  titel: string;
  soort: 'STANDAARD' | 'CUSTOM';
  vragen: VraagInvoer[];
  location_id: string | null;
  department_id: string | null;
  start_op: string | null;
  eind_op: string | null;
  kanaal_email: boolean;
  kanaal_sms: boolean;
  communicatie: CommunicatieBericht[];
}

export interface CampagneStandaarden {
  max_custom_vragen: number;
  sms_max: number;
  vooraankondiging_dagen: number;
  herinnering_dagen: number;
  herinnering_uur: number;
  livegang_uur: number;
  placeholders: string[];
  knop_label: Record<Moment, string | null>;
  link_lengte: number;
  portaal_link_lengte: number;
  standaard_vragen: { volgorde: number; type: 'STERREN' | 'OPEN'; tekst: string; label_laag: string | null; label_hoog: string | null }[];
  communicatie: CommunicatieBericht[];
}

// ─── Submap 5 ─────────────────────────────────────────────────────────────────

export interface PoolData {
  periode: Periode;
  grendel: Grendel;
  totaal: number | null;
  totaal_uren: number | null;
  segmenten: { discipline: string; label: string; meldingen: number; uren: number }[];
  overig: { meldingen: number | null; uren: number | null; onderdrukt: boolean } | null;
  per_afdeling: { discipline: string; label: string; afdeling: string; meldingen: number; samengevoegd: boolean }[];
  feed: { datum: string; code: string; discipline: string; gekoppeld: boolean }[];
}

export interface ReviewScores {
  aantal: number;
  /** Aantal door de AI-Sanitizer vrijgegeven teksten (0 zolang de laag vergrendeld is). */
  teksten: number;
  vakkennis: number | null;
  punctualiteit: number | null;
  doelgerichtheid: number | null;
  gemiddeld: number | null;
  onderdrukt: boolean;
}

export type ReviewLaag = 'intern' | 'landelijk';

export interface ReviewTeksten {
  laag: ReviewLaag;
  aantal_reviews: number;
  teksten: string[];
}

export interface ReviewRij {
  professional_id: string;
  naam: string;
  discipline: string | null;
  intern: ReviewScores;
  landelijk: ReviewScores;
}

export interface ReviewsData {
  periode: Periode;
  minimum_reviews: number;
  /** Landelijke laag: minimaal aantal andere organisaties. */
  minimum_organisaties: number;
  dimensies: { key: 'vakkennis' | 'punctualiteit' | 'doelgerichtheid'; label: string }[];
  intern: ReviewRij[];
  landelijk: ReviewRij[];
}

// ─── Calls ────────────────────────────────────────────────────────────────────

export const getVerzuim = (p: Periode, afdeling?: string | null) =>
  api.get<{ data: VerzuimData }>(`${BASE}/verzuim`, { params: { ...p, afdeling: afdeling || undefined } }).then(d);
export const getCrisis = (p: Periode) => api.get<{ data: CrisisData }>(`${BASE}/crisis`, { params: p }).then(d);
export const getBudgetten = (p: Periode) => api.get<{ data: BudgettenData }>(`${BASE}/budgetten`, { params: p }).then(d);
export const getHittekaartBronnen = (p: Periode) => api.get<{ data: HittekaartBron[] }>(`${BASE}/cultuur/bronnen`, { params: p }).then(d);
export const getHittekaart = (soort: 'campagne' | 'thermometer', id: string) =>
  api.get<{ data: Hittekaart }>(`${BASE}/cultuur/hittekaart`, { params: { soort, id } }).then(d);
export const getOpenAntwoorden = (id: string) => api.get<{ data: OpenAntwoorden }>(`${BASE}/cultuur/campagnes/${id}/open`).then(d);
export const getPool = (p: Periode) => api.get<{ data: PoolData }>(`${BASE}/pool`, { params: p }).then(d);
export const getReviews = (p: Periode) => api.get<{ data: ReviewsData }>(`${BASE}/reviews`, { params: p }).then(d);
export const getReviewTeksten = (professionalId: string, laag: ReviewLaag, p: Periode) =>
  api.get<{ data: ReviewTeksten }>(`${BASE}/reviews/${professionalId}/teksten`, { params: { ...p, laag } }).then(d);

export const getCampagnes = () =>
  api.get<{ data: { module_actief: boolean; campagnes: CampagneRij[]; mag_beheren: boolean } }>(`${BASE}/campagnes`).then(d);
export const getCampagneStandaarden = () => api.get<{ data: CampagneStandaarden }>(`${BASE}/campagnes/standaarden`).then(d);
export const getCampagne = (id: string) => api.get<{ data: CampagneDetail }>(`${BASE}/campagnes/${id}`).then(d);
export const maakCampagne = (body: CampagneInvoer) => api.post<{ data: { id: string } }>(`${BASE}/campagnes`, body).then(d);
export const werkCampagneBij = (id: string, body: CampagneInvoer) => api.put<{ data: { id: string } }>(`${BASE}/campagnes/${id}`, body).then(d);
export const planCampagne = (id: string) => api.post<{ data: { id: string } }>(`${BASE}/campagnes/${id}/plannen`).then(d);
export const annuleerCampagne = (id: string) => api.post<{ data: { id: string } }>(`${BASE}/campagnes/${id}/annuleren`).then(d);
export const verwijderCampagne = (id: string) => api.delete<{ data: { id: string } }>(`${BASE}/campagnes/${id}`).then(d);
